import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import BasePageLayout from '../../components/BasePageLayout';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { TeamService } from '../../services/teamService';
import { TeamRequest, TeamApplication } from '../../types/team';
import { useDeviceType, DeviceType } from '../../hooks/useDeviceType';
import { showToast } from '../../utils/toast';
import '../../styles/shared/dashboard.css';
import '../../styles/pages/TeamsPage.css';
import '../../styles/pages/ApplicationDetails.css';
import '../../styles/pages/TasksHubModern.css';
import '../../styles/pages/CompactTasksHeader.css';
import '../../styles/components/TaskCardWrapper.css';
import '../../styles/components/ApplicationForm.css';

type JoinTeamTab = 'available' | 'submitted' | 'completed' | 'rejected';

const JoinTeamPage: React.FC = () => {
  const { user } = useAuth();
  const { t, uiLanguage } = useLanguage();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<JoinTeamTab>('available');
  const { deviceType } = useDeviceType();
  const [selectedTeamRequest, setSelectedTeamRequest] = useState<TeamRequest | null>(null);
  // Team detail panel state
  const [showTeamDetail, setShowTeamDetail] = useState(false);
  const [stats, setStats] = useState({
    available: 0,
    submitted: 0,
    completed: 0,
    rejected: 0
  });

  // Data states
  const [teamRequests, setTeamRequests] = useState<TeamRequest[]>([]);
  const [myApplications, setMyApplications] = useState<TeamApplication[]>([]);

  // Filter states
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [skillFilter, setSkillFilter] = useState<string>('');

  // Application form states
  const [isApplying, setIsApplying] = useState<boolean>(false);
  const [applicationMessage, setApplicationMessage] = useState<string>('');
  const [whyFitMessage, setWhyFitMessage] = useState<string>('');
  const [skillsHighlight, setSkillsHighlight] = useState<string[]>([]);
  const [skillsInput, setSkillsInput] = useState<string>('');
  const [availability, setAvailability] = useState<string>('');

  // Application management states
  const [selectedApplication, setSelectedApplication] = useState<TeamApplication | null>(null);
  const [fullTeamRequest, setFullTeamRequest] = useState<TeamRequest | null>(null);

  // Responsive state
  const [isCompactView, setIsCompactView] = useState(false);

  const loadData = useCallback(async () => {
    if (!user) return;

    try {
      const [availableTeamsData, myApplicationsData] = await Promise.all([
        TeamService.getAvailableTeamsForUser(user.id, { search: searchTerm }),
        TeamService.getMyApplications(user.id)
      ]);

      setTeamRequests(availableTeamsData || []);
      setMyApplications(myApplicationsData || []);

      // Calculate stats
      const submittedCount = myApplicationsData?.filter(app => app.status === 'pending').length || 0;
      const completedCount = myApplicationsData?.filter(app => app.status === 'accepted').length || 0;
      const rejectedCount = myApplicationsData?.filter(app => app.status === 'rejected').length || 0;
      const availableCount = availableTeamsData?.length || 0;

      setStats({
        available: availableCount,
        submitted: submittedCount,
        completed: completedCount,
        rejected: rejectedCount
      });

      console.log('Join Team Data loaded:');
      console.log('- Available teams:', availableCount);
      console.log('- Submitted applications:', submittedCount);
      console.log('- Completed teams:', completedCount);
      console.log('- Rejected applications:', rejectedCount);

    } catch (error) {
      console.error('Error loading join team data:', error);
    }
  }, [user, searchTerm]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle URL parameter for pre-selecting a team
  useEffect(() => {
    const selectedTeamId = searchParams.get('selected');
    if (selectedTeamId && teamRequests.length > 0) {
      const teamToSelect = teamRequests.find(request => request.id === selectedTeamId);
      if (teamToSelect) {
        setSelectedTeamRequest(teamToSelect);
        setActiveTab('available');
      }
    }
  }, [searchParams, teamRequests]);

  // Set responsive states based on device type
  useEffect(() => {
    if (!deviceType) return;

    const isMobile = deviceType === DeviceType.MOBILE;
    const isTablet = deviceType === DeviceType.TABLET;

    setIsCompactView(isMobile || isTablet);
  }, [deviceType]);

  // Team Type Buttons Configuration
  const joinTeamTypeButtons = useMemo(() => [
    {
      id: 'available' as JoinTeamTab,
      label: t('teams.availableTeams'),
      hint: t('teams.teamsYouCanJoin'),
      icon: 'bi-people',
      count: stats.available,
      gradient: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
    },
    {
      id: 'submitted' as JoinTeamTab,
      label: t('teams.submitted'),
      hint: t('teams.applicationsWaitingReview'),
      icon: 'bi-clock-history',
      count: stats.submitted,
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
    },
    {
      id: 'completed' as JoinTeamTab,
      label: t('teams.completed'),
      hint: t('teams.successfullyJoinedTeams'),
      icon: 'bi-check-circle-fill',
      count: stats.completed,
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
    },
    {
      id: 'rejected' as JoinTeamTab,
      label: t('teams.rejected'),
      hint: t('teams.applicationsThatNeedAttention'),
      icon: 'bi-x-circle-fill',
      count: stats.rejected,
      gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
    }
  ], [t, stats.available, stats.submitted, stats.completed, stats.rejected]);

  // Scroll effect for compact header
  useEffect(() => {
    const handleScroll = () => {
      const header = document.querySelector('.compact-tasks-header');
      if (header) {
        if (window.scrollY > 10) {
          header.classList.add('scrolled');
        } else {
          header.classList.remove('scrolled');
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Call once on mount

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleTeamRequestClick = useCallback((teamRequest: TeamRequest) => {
    // Keep the team visible in the list when selected
    setSelectedTeamRequest(prevSelected => prevSelected?.id === teamRequest.id ? prevSelected : teamRequest);
    setShowTeamDetail(true);
  }, []);

  const handleApplicationClick = useCallback(async (application: TeamApplication) => {
    // Clear team request selection and set application selection
    setSelectedTeamRequest(null);
    setSelectedApplication(prevSelected => prevSelected?.id === application.id ? prevSelected : application);
    setShowTeamDetail(true);
    
    // Fetch full team request details if we have the ID
    if (application.team_request_id) {
      try {
        const fullRequest = teamRequests.find(tr => tr.id === application.team_request_id);
        if (fullRequest) {
          setFullTeamRequest(fullRequest);
        } else {
          // If not in current list, you could fetch from API here
          setFullTeamRequest(null);
        }
      } catch (error) {
        console.error('Error fetching full team request:', error);
        setFullTeamRequest(null);
      }
    }
  }, [teamRequests]);

  const handleCloseDetails = () => {
    setSelectedTeamRequest(null);
    setSelectedApplication(null);
    setFullTeamRequest(null);
    setShowTeamDetail(false);
  };

  // Withdraw Application Handler
  const handleWithdrawApplication = async (applicationId: string) => {
    if (!window.confirm('Are you sure you want to withdraw this application?')) return;

    try {
      await TeamService.withdrawApplication(applicationId);
      await loadData();
      showToast('Application withdrawn successfully.', 'success');
    } catch (error) {
      console.error('Error withdrawing application:', error);
      showToast('Failed to withdraw application. Please try again.', 'error');
    }
  };

  // Enhanced Team Application Handler
  const handleApplyToTeam = async (teamRequestId: string) => {
    if (!user) return;

    setIsApplying(true);
    try {
      // Check if user has already applied
      const existingApplication = myApplications.find(
        app => app.team_request_id === teamRequestId
      );

      if (existingApplication) {
        showToast('You have already applied to this team.', 'warning');
        return;
      }

      // Validate required fields
      if (!applicationMessage.trim() || !whyFitMessage.trim()) {
        showToast('Please fill in both the introduction and why you fit messages.', 'warning');
        return;
      }

      const applicationData = {
        team_request_id: teamRequestId,
        introduction: applicationMessage.trim(),
        why_fit: whyFitMessage.trim(),
        skills_highlight: skillsHighlight.length > 0 ? skillsHighlight : ['General Skills'],
        availability: availability || 'Available as needed'
      };

      await TeamService.createApplication(applicationData);

      // Clear form
      setApplicationMessage('');
      setWhyFitMessage('');
      setSkillsHighlight([]);
      setSkillsInput('');
      setAvailability('');

      // Refresh data
      await loadData();

      // Show success message
      showToast('Application submitted successfully! The team publisher will review your application.', 'success');

      // Switch to submitted tab
      setActiveTab('submitted');

    } catch (error) {
      console.error('Error applying to team:', error);
      showToast('Failed to submit application. Please try again.', 'error');
    } finally {
      setIsApplying(false);
    }
  };

  // Add skills to highlight
  const handleAddSkill = () => {
    if (skillsInput.trim() && !skillsHighlight.includes(skillsInput.trim())) {
      setSkillsHighlight([...skillsHighlight, skillsInput.trim()]);
      setSkillsInput('');
    }
  };

  const handleRemoveSkill = (skillToRemove: string) => {
    setSkillsHighlight(skillsHighlight.filter(skill => skill !== skillToRemove));
  };

  // Check if user has already applied to a team
  const hasUserApplied = (teamRequestId: string): boolean => {
    return myApplications.some(app => app.team_request_id === teamRequestId);
  };

  // Filter data based on active tab
  const getCurrentApplications = (): TeamApplication[] => {
    switch (activeTab) {
      case 'submitted':
        return myApplications.filter(app => app.status === 'pending');
      case 'completed':
        return myApplications.filter(app => app.status === 'accepted');
      case 'rejected':
        return myApplications.filter(app => app.status === 'rejected');
      default:
        return [];
    }
  };

  const handleTabSwitch = useCallback((tab: JoinTeamTab) => {
    setActiveTab(tab);
    setSelectedTeamRequest(null);
    setSelectedApplication(null);
    setFullTeamRequest(null);
  }, []);

  return (
    <BasePageLayout background="white" padding="none" maxWidth="none">
      <div className="tasks-hub-container">
        {/* Compact Header */}
        <div className="compact-tasks-header">
          <div className="compact-header-content">
            {/* Title Section */}
            <div className="compact-title-section">
              <div>
                <h1 className="compact-page-title">{t('teams.joinTeam')}</h1>
                <div className="compact-breadcrumb">
                  <span>{t('navigation.teams')}</span>
                  <i className="bi bi-chevron-right compact-breadcrumb-separator"></i>
                  <span>{t('teams.joinTeam')}</span>
                </div>
              </div>
              <div className="compact-stats-mini">
                <div className="stat-item-mini">
                  <span className="stat-number-mini">{stats.available + stats.submitted + stats.completed + stats.rejected}</span>
                  <span className="stat-label-mini">{t('teams.totalApplications')}</span>
                </div>
                <div className="stat-item-mini earnings">
                  <span className="stat-number-mini">{stats.completed}</span>
                  <span className="stat-label-mini">{t('teams.teamsJoined')}</span>
                </div>
              </div>
            </div>

            {/* Controls Section */}
            <div className="compact-controls-section">
              {/* Team Type Buttons */}
              <div className="compact-type-buttons">
                {joinTeamTypeButtons.map(button => (
                  <button
                    key={button.id}
                    className={`compact-type-button compact-tooltip ${activeTab === button.id ? 'active' : ''} ${button.count === 0 ? 'disabled' : ''}`}
                    onClick={() => button.count > 0 && handleTabSwitch(button.id)}
                    disabled={button.count === 0}
                    data-tooltip={button.hint}
                    style={{
                      background: activeTab === button.id ? button.gradient : undefined
                    }}
                  >
                    <i className={`bi ${button.icon} compact-type-button-icon`}></i>
                    <span className="compact-type-button-label">{button.label}</span>
                    <span className="compact-type-button-count">{button.count}</span>
                  </button>
                ))}
              </div>

              {/* Search and Filters */}
              {activeTab === 'available' && (
                <div className="compact-search-filters">
                  <div className="compact-search-bar">
                    <i className="bi bi-search compact-search-icon"></i>
                    <input
                      type="text"
                      className="compact-search-input"
                      placeholder={t('teams.searchTeamsPlaceholder')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  {skillFilter && (
                    <div className="compact-filter-dropdown">
                      <input
                        type="text"
                        placeholder={t('teams.requiredSkills')}
                        value={skillFilter}
                        onChange={(e) => setSkillFilter(e.target.value)}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className={`tasks-hub-main-two-column ${showTeamDetail ? 'detail-panel-open' : ''}`}>
          {/* Content List */}
          <div className="tasks-hub-task-list-column">
            <div className="tasks-hub-task-list-header">
              <span className="tasks-hub-results-count">
                {activeTab === 'available' ? 
                  `${teamRequests.length} ${teamRequests.length === 1 ? t('teams.team') : t('teams.teams')}` :
                  `${getCurrentApplications().length} ${getCurrentApplications().length === 1 ? t('teams.application') : t('teams.applications')}`
                }
              </span>
              <div className="tasks-hub-sort-controls">
                <button
                  onClick={loadData}
                  className="tasks-hub-refresh-button"
                  title={t('teams.refresh')}
                >
                  <i className="bi bi-arrow-clockwise"></i>
                </button>
              </div>
            </div>

            <div className="tasks-hub-task-list-content">
              {/* Content based on active tab */}
              {activeTab === 'available' && (
                <>
                  {teamRequests.length === 0 ? (
                    <div className="tasks-hub-empty-state">
                      <div className="tasks-hub-empty-icon">🔍</div>
                      <h4>{t('teams.noTeamsAvailableMessage')}</h4>
                      <p>{t('teams.checkBackLater')}</p>
                    </div>
                  ) : (
                    <div className="tasks-hub-task-cards">
                      {teamRequests.map(teamRequest => (
                        <div
                          key={teamRequest.id}
                          className={`task-card-wrapper ${selectedTeamRequest?.id === teamRequest.id ? 'selected' : ''}`}
                          onClick={() => handleTeamRequestClick(teamRequest)}
                        >
                          <div className="task-card task-card-modern">
                            <div className="task-header-row">
                              <h3 className="task-title">{teamRequest.title}</h3>
                              <div className="task-status-badges">
                                <span className="task-points">
                                  <i className="bi bi-people-fill"></i>
                                  {t('teams.teamSizeMembers', { size: teamRequest.team_size })}
                                </span>
                              </div>
                            </div>

                            <p className="task-description">{teamRequest.project_introduction}</p>

                            <div className="task-meta">
                              <div className="task-meta-left">
                                <span className="task-category">
                                  <i className="bi bi-briefcase"></i>
                                  {teamRequest.skills_required?.slice(0, 2).join(', ') || t('tasks.generalSkills')}
                                </span>
                                <span className="task-verification-info">
                                  <i className="bi bi-clock"></i>
                                  {teamRequest.min_time_commitment}
                                </span>
                                <span className="task-participants">
                                  <i className="bi bi-star-fill"></i>
                                  {teamRequest.points_cost} {t('teams.points')}
                                </span>
                              </div>
                              <div className="task-meta-right">
                                <span className="task-date">
                                  {new Date(teamRequest.created_at).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* Applications tabs (submitted, completed, rejected) */}
              {(activeTab === 'submitted' || activeTab === 'completed' || activeTab === 'rejected') && (
                <>
                  {getCurrentApplications().length === 0 ? (
                    <div className="tasks-hub-empty-state">
                      <div className="tasks-hub-empty-icon">
                        {activeTab === 'submitted' && '⏳'}
                        {activeTab === 'completed' && '🎉'}
                        {activeTab === 'rejected' && '📝'}
                      </div>
                      <h4>{t('teams.noPendingApplications')}</h4>
                      <p>
                        {activeTab === 'submitted' && t('teams.noPendingApplicationsMessage')}
                        {activeTab === 'completed' && t('teams.noCompletedTeamMemberships')}
                        {activeTab === 'rejected' && t('teams.noRejectedApplications')}
                      </p>
                      {activeTab === 'submitted' && (
                        <button 
                          className="btn btn-primary"
                          onClick={() => handleTabSwitch('available')}
                        >
                          <i className="bi bi-search"></i>
                          {t('teams.browseAvailableTeams')}
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="tasks-hub-task-cards">
                      {getCurrentApplications().map((application: TeamApplication) => (
                        <div
                          key={application.id}
                          className={`task-card-wrapper ${selectedApplication?.id === application.id ? 'selected' : ''}`}
                          onClick={() => handleApplicationClick(application)}
                        >
                          <div className="task-card task-card-modern application-task-card">
                            <div className="task-header-row">
                              <h3 className="task-title">{application.team_request?.title}</h3>
                              <div className="task-status-badges">
                                <span className={`task-status-badge ${application.status}`}>
                                  <i className={`bi ${
                                    application.status === 'accepted' ? 'bi-check-circle-fill' :
                                    application.status === 'pending' ? 'bi-clock-history' :
                                    'bi-x-circle-fill'
                                  }`}></i>
                                  {application.status.toUpperCase()}
                                </span>
                              </div>
                            </div>

                            <p className="task-description">{application.introduction}</p>

                            <div className="task-meta">
                              <div className="task-meta-left">
                                <span className="task-category">
                                  <i className="bi bi-calendar3"></i>
                                  {t('teams.applied')} {application.applied_at ? new Date(application.applied_at).toLocaleDateString() : t('common.dateNotAvailable')}
                                </span>
                                {application.reviewed_at && (
                                  <span className="task-verification-info">
                                    <i className="bi bi-check2-circle"></i>
                                    {t('teams.reviewed')} {new Date(application.reviewed_at).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                              <div className="task-meta-right">
                                {application.status === 'pending' && (
                                  <button
                                    className="btn btn-sm btn-outline-danger withdraw-button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleWithdrawApplication(application.id);
                                    }}
                                  >
                            <i className="bi bi-x-circle"></i>
                                    {t('teams.withdraw')}
                                  </button>
                                )}
                              </div>
                            </div>

                            {application.publisher_message && (
                              <div className="application-feedback">
                                <div className="feedback-label">
                                  <i className="bi bi-chat-text"></i>
                                  {t('teams.publishersMessage')}
                                </div>
                                <div className="feedback-content">
                                  {application.publisher_message}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Overlay for when detail panel is open */}
          {showTeamDetail && (
            <div className="team-detail-overlay" onClick={handleCloseDetails}></div>
          )}

          {/* Team Detail Panel - Slide out from right */}
          <div className={`team-detail-panel ${showTeamDetail ? 'panel-open' : ''}`}>
            {selectedTeamRequest ? (
              /* Available Team Request Details */
              <div className="team-detail-content">
                <div className="team-detail-header">
                  <div className="team-detail-header-main">
                    <div className="team-detail-title-area">
                      <h1 className="team-detail-title">{selectedTeamRequest.title}</h1>
                      <div className="team-detail-metadata">
                        <span className="team-detail-category-badge available-team">
                          <i className="bi bi-people-fill"></i>
                          {t('teams.teamSize')}: {selectedTeamRequest.team_size}
                        </span>
                        <span className="team-detail-date-badge">
                          <i className="bi bi-calendar3"></i>
                          {t('teams.created')} {new Date(selectedTeamRequest.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric'
                          })}
                        </span>
                        <span className="team-detail-points-badge">
                          <i className="bi bi-star-fill"></i>
                          {selectedTeamRequest.points_cost} {t('teams.points')}
                        </span>
                      </div>
                    </div>
                    
                    <div className="team-detail-header-actions">
                      <button 
                        className="task-details-action-btn new-tab"
                        onClick={() => window.open(`/join-team?teamId=${selectedTeamRequest.id}`, '_blank')}
                        title={t('teams.openInNewTab')}
                      >
                        <i className="bi bi-box-arrow-up-right"></i>
                      </button>
                      <button 
                        className="task-details-action-btn close"
                        onClick={handleCloseDetails}
                        title={t('teams.close')}
                      >
                        <i className="bi bi-x-lg"></i>
                      </button>
                    </div>
                  </div>
                </div>
                <div className="task-details-content">
                  {/* Project Description */}
                  <section className="task-details-section">
                    <div className="task-details-section-header">
                      <h2 className="task-details-section-title">
                        <i className="bi bi-file-text"></i>
                        {t('teams.projectDescription')}
                      </h2>
                    </div>
                    <div className="task-details-section-content">
                      <p className="task-details-description">{selectedTeamRequest.project_introduction}</p>
                    </div>
                  </section>

                  {/* Requirements */}
                    <section className="task-details-section">
                      <div className="task-details-section-header">
                        <h2 className="task-details-section-title">
                          <i className="bi bi-list-check"></i>
                          {t('teams.requirements')}
                        </h2>
                      </div>
                      <div className="task-details-section-content">
                        <div className="requirement-items">
                          <div className="requirement-item">
                            <i className="bi bi-clock"></i>
                            <span>{t('teams.timeCommitment')}: {selectedTeamRequest?.min_time_commitment}</span>
                          </div>
                          {selectedTeamRequest?.deadline && (
                            <div className="requirement-item">
                              <i className="bi bi-calendar-check"></i>
                              <span>{t('teams.deadline')}: {new Date(selectedTeamRequest.deadline).toLocaleDateString()}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </section>

                    {/* Required Skills */}
                    {selectedTeamRequest?.skills_required && selectedTeamRequest.skills_required.length > 0 && (
                      <section className="task-details-section">
                        <div className="task-details-section-header">
                          <h2 className="task-details-section-title">
                            <i className="bi bi-gear-fill"></i>
                            {t('teams.requiredSkills')}
                          </h2>
                        </div>
                        <div className="task-details-section-content">
                          <div className="task-details-tags">
                            {selectedTeamRequest.skills_required.map((skill, index) => (
                              <span key={index} className="task-details-tag">
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      </section>
                    )}

                    {/* Application Form */}
                    {selectedTeamRequest && !hasUserApplied(selectedTeamRequest.id) ? (
                      <section className="task-details-section application-form-section">
                        <div className="task-details-section-header">
                          <h2 className="task-details-section-title">
                            <i className="bi bi-send"></i>
                            {t('teams.applyToTeamButton')}
                          </h2>
                        </div>
                        <div className="task-details-section-content">
                          <form className="application-form" onSubmit={(e) => {
                            e.preventDefault();
                            if (selectedTeamRequest) {
                              handleApplyToTeam(selectedTeamRequest.id);
                            }
                          }}>
                            {/* Introduction */}
                            <div className="form-group">
                              <label htmlFor="application-message" className="form-label">
                                <i className="bi bi-person"></i>
                                {t('teams.introductionLabel')} *
                              </label>
                              <textarea
                                id="application-message"
                                className="form-control"
                                rows={4}
                                placeholder={t('teams.introductionPlaceholder')}
                                value={applicationMessage}
                                onChange={(e) => setApplicationMessage(e.target.value)}
                                required
                              />
                            </div>

                            {/* Why You Fit */}
                            <div className="form-group">
                              <label htmlFor="why-fit-message" className="form-label">
                                <i className="bi bi-star"></i>
                                {t('teams.whyFitLabel')} *
                              </label>
                              <textarea
                                id="why-fit-message"
                                className="form-control"
                                rows={4}
                                placeholder={t('teams.whyFitPlaceholder')}
                                value={whyFitMessage}
                                onChange={(e) => setWhyFitMessage(e.target.value)}
                                required
                              />
                            </div>

                            {/* Skills Highlight */}
                            <div className="form-group">
                              <label className="form-label">
                                <i className="bi bi-gear"></i>
                                {t('teams.skillsHighlightLabel')}
                              </label>
                              <div className="skills-input-group">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder={t('teams.skillsHighlightPlaceholder')}
                                  value={skillsInput}
                                  onChange={(e) => setSkillsInput(e.target.value)}
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      handleAddSkill();
                                    }
                                  }}
                                />
                                <button
                                  type="button"
                                  className="btn btn-outline-primary"
                                  onClick={handleAddSkill}
                                  disabled={!skillsInput.trim()}
                                >
                                  {t('teams.add')}
                                </button>
                              </div>
                              {skillsHighlight.length > 0 && (
                                <div className="skills-display">
                                  {skillsHighlight.map((skill, index) => (
                                    <span key={index} className="skill-badge">
                                      {skill}
                                      <button
                                        type="button"
                                        className="skill-remove"
                                        onClick={() => handleRemoveSkill(skill)}
                                      >
                                        ×
                                      </button>
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* Availability */}
                            <div className="form-group">
                              <label htmlFor="availability" className="form-label">
                                <i className="bi bi-clock"></i>
                                {t('teams.availabilityLabel')}
                              </label>
                              <select
                                id="availability"
                                className="form-control"
                                value={availability}
                                onChange={(e) => setAvailability(e.target.value)}
                              >
                                <option value="">{t('teams.selectAvailability')}</option>
                                <option value="Full-time (40+ hours/week)">{t('teams.availabilityFullTime')}</option>
                                <option value="Part-time (20-40 hours/week)">{t('teams.availabilityPartTime')}</option>
                                <option value="Occasional (10-20 hours/week)">{t('teams.availabilityOccasional')}</option>
                                <option value="Flexible schedule">{t('teams.availabilityFlexible')}</option>
                                <option value="Weekends only">{t('teams.availabilityWeekends')}</option>
                                <option value="Evenings only">{t('teams.availabilityEvenings')}</option>
                              </select>
                            </div>

                            {/* Submit Button */}
                            <div className="form-actions">
                              <button
                                type="submit"
                                className="btn btn-primary btn-lg"
                                disabled={isApplying || !applicationMessage.trim() || !whyFitMessage.trim()}
                              >
                                {isApplying ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    {t('teams.submitting')}
                                  </>
                                ) : (
                                  <>
                                    <i className="bi bi-send me-2"></i>
                                    {t('teams.submitApplication')}
                                  </>
                                )}
                              </button>
                            </div>
                          </form>
                        </div>
                      </section>
                    ) : (
                      /* Already Applied Message */
                      <section className="task-details-section">
                        <div className="task-details-section-header">
                          <h2 className="task-details-section-title">
                            <i className="bi bi-check-circle"></i>
                            {t('teams.status')}
                          </h2>
                        </div>
                        <div className="task-details-section-content">
                          <div className="application-status-message">
                            <i className="bi bi-info-circle"></i>
                            <p>{t('teams.alreadyAppliedMessage')}</p>
                            <button 
                              className="btn btn-outline-primary"
                              onClick={() => handleTabSwitch('submitted')}
                            >
                              {t('teams.viewApplications')}
                            </button>
                          </div>
                        </div>
                      </section>
                    )}
                  </div>
                </div>
              ) : selectedApplication ? (
                /* Application Details View - Enhanced for Different States */
                <div className="team-detail-content application-detail-view">
                  <div className="team-detail-header">
                    <div className="team-detail-header-main">
                      <div className="team-detail-title-area">
                        <h1 className="team-detail-title">
                          {selectedApplication?.team_request?.title || t('teams.applicationStatus')}
                        </h1>
                        <div className="team-detail-metadata">
                          <span className={`team-detail-status-badge ${selectedApplication?.status}`}>
                            <i className={`bi ${
                              selectedApplication?.status === 'accepted' ? 'bi-check-circle-fill' :
                              selectedApplication?.status === 'pending' ? 'bi-clock-history' :
                              'bi-x-circle-fill'
                            }`}></i>
                            {selectedApplication?.status === 'accepted' ? t('teams.completed').toUpperCase() :
                             selectedApplication?.status === 'pending' ? t('teams.pending').toUpperCase() :
                             t('teams.rejected').toUpperCase()}
                          </span>
                          <span className="team-detail-date-badge">
                            <i className="bi bi-calendar3"></i>
                            {t('teams.applied')} {selectedApplication?.applied_at ? new Date(selectedApplication.applied_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            }) : t('common.dateNotAvailable')}
                          </span>
                          {selectedApplication?.reviewed_at && (
                            <span className="team-detail-review-badge">
                              <i className="bi bi-check2-circle"></i>
                              {t('teams.reviewed')} {new Date(selectedApplication.reviewed_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric'
                              })}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="team-detail-header-actions">
                        {selectedApplication?.team_request?.id && (
                          <button 
                            className="task-details-action-btn new-tab"
                            onClick={() => window.open(`/join-team?teamId=${selectedApplication.team_request?.id}`, '_blank')}
                            title={t('teams.viewOriginalTeamRequest')}
                          >
                            <i className="bi bi-box-arrow-up-right"></i>
                          </button>
                        )}
                        <button 
                          className="task-details-action-btn close"
                          onClick={handleCloseDetails}
                          title={t('teams.close')}
                        >
                          <i className="bi bi-x-lg"></i>
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="task-details-content">
                    {/* Application Status Overview */}
                    <section className="task-details-section application-status-overview">
                      <div className="task-details-section-header">
                        <h2 className="task-details-section-title">
                          <i className={`bi ${
                            selectedApplication?.status === 'accepted' ? 'bi-check-circle-fill' :
                            selectedApplication?.status === 'pending' ? 'bi-clock-history' :
                            'bi-x-circle-fill'
                          }`}></i>
                          {t('teams.applicationStatus')}
                        </h2>
                      </div>
                      <div className="task-details-section-content">
                        <div className={`application-status-card ${selectedApplication?.status}`}>
                          {selectedApplication?.status === 'accepted' && (
                            <div className="status-message success">
                              <div className="status-icon">🎉</div>
                              <div className="status-text">
                                <h4>{t('teams.congratulationsJoined')}</h4>
                                <p>{t('teams.applicationAcceptedMessage')}</p>
                              </div>
                            </div>
                          )}
                          {selectedApplication?.status === 'pending' && (
                            <div className="status-message pending">
                              <div className="status-icon">⏳</div>
                              <div className="status-text">
                                <h4>{t('teams.applicationUnderReview')}</h4>
                                <p>{t('teams.applicationUnderReviewMessage')}</p>
                              </div>
                            </div>
                          )}
                          {selectedApplication?.status === 'rejected' && (
                            <div className="status-message rejected">
                              <div className="status-icon">📝</div>
                              <div className="status-text">
                                <h4>{t('teams.applicationNotSelected')}</h4>
                                <p>{t('teams.applicationNotSelectedMessage')}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </section>

                    {/* Team Request Information */}
                    <section className="task-details-section">
                      <div className="task-details-section-header">
                        <h2 className="task-details-section-title">
                          <i className="bi bi-people-fill"></i>
                          {t('teams.teamRequestDetails')}
                        </h2>
                      </div>
                      <div className="task-details-section-content">
                        <div className="team-request-summary">
                          <h4>{selectedApplication?.team_request?.title}</h4>
                          <p className="task-details-description">
                            {selectedApplication?.team_request?.project_introduction}
                          </p>
                          <div className="team-request-meta">
                            <div className="meta-item">
                              <i className="bi bi-people"></i>
                              <span>{t('teams.teamSizeLabel')} {fullTeamRequest?.team_size || t('teams.notSpecified')}</span>
                            </div>
                            <div className="meta-item">
                              <i className="bi bi-clock"></i>
                              <span>{t('teams.timeCommitmentLabel')} {fullTeamRequest?.min_time_commitment || t('teams.notSpecified')}</span>
                            </div>
                            <div className="meta-item">
                              <i className="bi bi-star-fill"></i>
                              <span>{t('teams.pointsLabel')} {fullTeamRequest?.points_cost || t('teams.notSpecified')}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </section>

                    {/* Your Application Details */}
                    <section className="task-details-section">
                      <div className="task-details-section-header">
                        <h2 className="task-details-section-title">
                          <i className="bi bi-file-text"></i>
                          {t('teams.yourApplication')}
                        </h2>
                      </div>
                      <div className="task-details-section-content">
                        <div className="application-details-grid">
                          {/* Introduction */}
                          <div className="application-detail-item">
                            <h5>
                              <i className="bi bi-person"></i>
                              {t('teams.introductionHeading')}
                            </h5>
                            <p className="task-details-description">{selectedApplication?.introduction}</p>
                          </div>

                          {/* Why You Fit */}
                          <div className="application-detail-item">
                            <h5>
                              <i className="bi bi-star-fill"></i>
                              {t('teams.whyFitHeading')}
                            </h5>
                            <p className="task-details-description">{selectedApplication?.why_fit}</p>
                          </div>

                          {/* Skills Highlight */}
                          {selectedApplication?.skills_highlight && selectedApplication.skills_highlight.length > 0 && (
                            <div className="application-detail-item">
                              <h5>
                                <i className="bi bi-gear-fill"></i>
                                {t('teams.skillsHighlightedHeading')}
                              </h5>
                              <div className="task-details-tags">
                                {selectedApplication.skills_highlight.map((skill, index) => (
                                  <span key={index} className="task-details-tag">
                                    {skill}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Availability */}
                          {selectedApplication?.availability && (
                            <div className="application-detail-item">
                              <h5>
                                <i className="bi bi-clock"></i>
                                {t('teams.availabilityHeading')}
                              </h5>
                              <p className="task-details-description">{selectedApplication.availability}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </section>

                    {/* Publisher's Response/Feedback */}
                    {selectedApplication?.publisher_message && (
                      <section className="task-details-section publisher-feedback-section">
                        <div className="task-details-section-header">
                          <h2 className="task-details-section-title">
                            <i className="bi bi-chat-text"></i>
                            {t('teams.publishersMessageHeading')}
                          </h2>
                        </div>
                        <div className="task-details-section-content">
                          <div className="publisher-feedback-card">
                            <div className="feedback-header">
                              <i className="bi bi-person-circle"></i>
                              <span>{t('teams.teamPublisher')}</span>
                              {selectedApplication?.reviewed_at && (
                                <span className="feedback-date">
                                  {new Date(selectedApplication.reviewed_at).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                            <div className="feedback-content">
                              <p>{selectedApplication.publisher_message}</p>
                            </div>
                          </div>
                        </div>
                      </section>
                    )}

                    {/* Action Buttons Based on Status */}
                    <section className="task-details-section application-actions-section">
                      <div className="task-details-section-header">
                        <h2 className="task-details-section-title">
                          <i className="bi bi-gear"></i>
                          {t('teams.actionsHeading')}
                        </h2>
                      </div>
                      <div className="task-details-section-content">
                        <div className="application-actions">
                          {selectedApplication?.status === 'pending' && (
                            <>
                              <button
                                className="btn btn-outline-danger withdraw-button"
                                onClick={() => selectedApplication && handleWithdrawApplication(selectedApplication.id)}
                              >
                                <i className="bi bi-x-circle"></i>
                                {t('teams.withdrawApplicationButton')}
                              </button>
                              <div className="action-help-text">
                                <small>{t('teams.withdrawApplicationHelp')}</small>
                              </div>
                            </>
                          )}
                          
                          {selectedApplication?.status === 'accepted' && (
                            <>
                              <button
                                className="btn btn-success"
                                onClick={() => window.open(`/teams/${selectedApplication?.team_request?.id}`, '_blank')}
                              >
                                <i className="bi bi-people-fill"></i>
                                {t('teams.goToTeamDashboard')}
                              </button>
                              <div className="action-help-text">
                                <small>{t('teams.teamDashboardHelp')}</small>
                              </div>
                            </>
                          )}
                          
                          {selectedApplication?.status === 'rejected' && (
                            <>
                              <button
                                className="btn btn-primary"
                                onClick={() => handleTabSwitch('available')}
                              >
                                <i className="bi bi-search"></i>
                                {t('teams.findOtherTeams')}
                              </button>
                              <div className="action-help-text">
                                <small>{t('teams.findOtherTeamsHelp')}</small>
                              </div>
                            </>
                          )}
                          
                          {/* Common action for all states */}
                          <button
                            className="btn btn-outline-secondary"
                            onClick={() => {
                              if (fullTeamRequest) {
                                // Use the full team request data
                                setSelectedTeamRequest(fullTeamRequest);
                                setSelectedApplication(null);
                                setActiveTab('available');
                              } else if (selectedApplication?.team_request?.id) {
                                // Try to find in current team requests
                                const teamRequest = teamRequests.find(tr => tr.id === selectedApplication.team_request?.id);
                                if (teamRequest) {
                                  setSelectedTeamRequest(teamRequest);
                                  setSelectedApplication(null);
                                  setActiveTab('available');
                                } else {
                                  // Open in new tab if we can't find the full data
                                  window.open(`/join-team?teamId=${selectedApplication.team_request.id}`, '_blank');
                                }
                              }
                            }}
                          >
                            <i className="bi bi-info-circle"></i>
                            {t('teams.viewFullTeamRequest')}
                          </button>
                        </div>
                      </div>
                    </section>

                    {/* Application Timeline */}
                    <section className="task-details-section application-timeline-section">
                      <div className="task-details-section-header">
                        <h2 className="task-details-section-title">
                          <i className="bi bi-clock-history"></i>
                          {t('teams.applicationTimeline')}
                        </h2>
                      </div>
                      <div className="task-details-section-content">
                        <div className="application-timeline">
                          <div className="timeline-item completed">
                            <div className="timeline-marker">
                              <i className="bi bi-check-circle-fill"></i>
                            </div>
                            <div className="timeline-content">
                              <h6>{t('teams.applicationSubmittedLabel')}</h6>
                              <p>{selectedApplication?.applied_at ? new Date(selectedApplication.applied_at).toLocaleDateString(uiLanguage === 'zh' ? 'zh-CN' : 'en-US', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              }) : t('teams.dateNotAvailable')}</p>
                            </div>
                          </div>
                          
                          {selectedApplication?.reviewed_at && (
                            <div className={`timeline-item ${selectedApplication?.status !== 'pending' ? 'completed' : 'pending'}`}>
                              <div className="timeline-marker">
                                <i className={`bi ${selectedApplication?.status === 'accepted' ? 'bi-check-circle-fill' : selectedApplication?.status === 'rejected' ? 'bi-x-circle-fill' : 'bi-clock-history'}`}></i>
                              </div>
                              <div className="timeline-content">
                                <h6>{t('teams.applicationReviewed')}</h6>
                                <p>{new Date(selectedApplication.reviewed_at).toLocaleDateString(uiLanguage === 'zh' ? 'zh-CN' : 'en-US', {
                                  weekday: 'long',
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}</p>
                                <span className={`timeline-status ${selectedApplication?.status}`}>
                                  {selectedApplication?.status === 'accepted' ? t('teams.acceptedLabel') : t('teams.rejected')}
                                </span>
                              </div>
                            </div>
                          )}
                          
                          {selectedApplication?.status === 'pending' && (
                            <div className="timeline-item pending">
                              <div className="timeline-marker">
                                <i className="bi bi-clock-history"></i>
                              </div>
                              <div className="timeline-content">
                                <h6>{t('teams.awaitingReview')}</h6>
                                <p>{t('teams.awaitingReviewMessage')}</p>
                                <span className="timeline-status pending">{t('teams.inProgress')}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </section>
                  </div>
                </div>
              ) : null}
            </div>

        </div>
      </div>
    </BasePageLayout>
  );
};

export default JoinTeamPage;
